const META_DESCRIPTION_MAX = 155

const HTML_TAG = /<\/?[a-z][^<>]*>|<![^<>]*>/gi

export const plainDescription = (description: string): string =>
  description
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(HTML_TAG, " ")
    .replace(/[*_#`>]+/g, "")
    .replace(/\s+/g, " ")
    .trim()

const HTML_ENTITIES: Record<string, string> = {
  nbsp: " ",
  amp: "&",
  quot: '"',
  apos: "'",
  aacute: "á",
  eacute: "é",
  iacute: "í",
  oacute: "ó",
  uacute: "ú",
  ntilde: "ñ",
  uuml: "ü",
  Aacute: "Á",
  Eacute: "É",
  Iacute: "Í",
  Oacute: "Ó",
  Uacute: "Ú",
  Ntilde: "Ñ",
  Uuml: "Ü",
  iexcl: "¡",
  iquest: "¿",
  deg: "°",
  reg: "®",
  trade: "™",
  copy: "©",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
  laquo: "«",
  raquo: "»",
  bull: "•",
  middot: "·",
  times: "×",
  euro: "€",
  ordm: "º",
  ordf: "ª",
  sup2: "²",
  sup3: "³",
  frac12: "½",
  frac14: "¼",
  frac34: "¾",
}

const fromCode = (code: number): string => (code > 0 && code <= 0x10ffff && (code < 0xd800 || code > 0xdfff) ? String.fromCodePoint(code) : " ")

const decodeEntities = (text: string): string =>
  text
    .replace(/&#(\d+);/g, (_, code: string) => fromCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code: string) => fromCode(parseInt(code, 16)))
    .replace(/&([a-z][a-z0-9]*);/gi, (entity, name: string) => {
      const key = Object.hasOwn(HTML_ENTITIES, name) || name !== name.toUpperCase() ? name : name.toLowerCase()
      return Object.hasOwn(HTML_ENTITIES, key) ? HTML_ENTITIES[key]! : entity
    })

const tableRowsAsText = (text: string): string =>
  text.replace(/^[ \t]*\|.*\|[ \t]*\n(?=[ \t]*\|?[ \t]*:?-{2,})/gm, "").replace(/^[ \t]*\|.*\|[ \t]*$/gm, (row) => {
    const cells = row
      .split(/(?<!\\)\|/)
      .map((cell) => cell.trim())
      .filter(Boolean)
    if (cells.every((cell) => /^:?-+:?$/.test(cell))) return ""
    return cells.length === 2 ? `${cells[0]}: ${cells[1]}.` : cells.length ? `${cells.join(", ")}.` : ""
  })

export const snippetText = (text: string): string =>
  plainDescription(tableRowsAsText(decodeEntities(text.replace(/\r\n?/g, "\n"))))
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(HTML_TAG, " ")
    .replace(/\\/g, "")
    .replace(/\p{Emoji_Modifier}|\u{FE0F}|\u{200D}|\u{20E3}/gu, "")
    .replace(/(?![©®™])\p{Extended_Pictographic}|\p{Regional_Indicator}/gu, " · ")
    .replace(/:?-{3,}:?/g, " · ")
    .replace(/\s*--\s*|\s*[|•●▪■◆►▶➤➜→✓]\s*/g, " · ")
    .replace(/(\p{Lu}{3,})(\p{Lu}\p{Ll}{2,})/gu, "$1 $2")
    .replace(/\s+/g, " ")
    .replace(/(?:\s*·\s*){2,}/g, " · ")
    .replace(/([.!?:;])(?:\s*·)+/g, "$1")
    .replace(/(?<!\.)\.\.(?!\.)/g, ".")
    .replace(/([.!?])\s*[-–—]+(?=\s)/g, "$1")
    .replace(/^(?:[\s,;:!?·–—]|[.-](?!\d))+|[\s·]+$/g, "")
    .replace(/\s+([,.;:!?])(?=\s|$)/g, "$1")
    .trim()

const cutDescription = (flat: string, max = META_DESCRIPTION_MAX): string => {
  const chars = Array.from(flat)
  if (chars.length <= max) return flat

  const cut = chars.slice(0, max - 1).join("")
  const lastSpace = cut.lastIndexOf(" ")
  return `${(lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.·\-–—]+$/, "")}…`
}

export const metaDescription = (text: string, max = META_DESCRIPTION_MAX): string => cutDescription(plainDescription(text), max)

const SENTENCE_BREAK = /(?<=[.!?])\s+/
const CLAUSE_BREAK = /,\s|\s(?:y|and)\s/g

const charCount = (text: string): number => Array.from(text).length

const leadingSentences = (flat: string, max: number): string => {
  let kept = ""
  for (const sentence of flat.split(SENTENCE_BREAK)) {
    const next = kept ? `${kept} ${sentence}` : sentence
    if (charCount(next) > max) break
    kept = next
  }
  return kept
}

const leadingClauses = (flat: string, max: number): string => {
  const head = Array.from(flat).slice(0, max).join("")
  const lastBreak = Math.max(-1, ...Array.from(head.matchAll(CLAUSE_BREAK), (match) => match.index))
  if (lastBreak <= max / 2) return ""
  return `${head.slice(0, lastBreak).replace(/[\s,;:]+$/, "")}.`
}

const trimToSentence = (flat: string, max: number): string => {
  if (charCount(flat) <= max) return flat
  return leadingSentences(flat, max) || leadingClauses(flat, max) || cutDescription(flat, max)
}

export const fitDescription = (template: (text: string) => string, text: string, max = META_DESCRIPTION_MAX): string => {
  const fitted = template(trimToSentence(text, max - charCount(template(""))))
  return charCount(fitted) <= max ? fitted : cutDescription(fitted, max)
}
